import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  XCircleIcon, 
  CheckCircleIcon, 
  PlayIcon, 
  DocumentTextIcon 
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';

const CandidateResult = ({ candidate, interview, onCandidateUpdate }) => {
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [loadingFeedback, setLoadingFeedback] = useState({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [answers, setAnswers] = useState(candidate.answers || []);

  // Update answers when candidate prop changes
  useEffect(() => {
    setAnswers(candidate.answers || []);
  }, [candidate.answers]);

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateScore = (answers) => {
    if (!answers || answers.length === 0) return 0;
    const validScores = answers.filter(answer => answer.score !== null && answer.score !== undefined);
    if (validScores.length === 0) return 0;
    const totalScore = validScores.reduce((sum, answer) => sum + answer.score, 0);
    return Math.round(totalScore / validScores.length);
  };

  const generateSingleFeedback = async (answerId) => {
    try {
      setLoadingFeedback(prev => ({ ...prev, [answerId]: true }));
      
      const result = await apiService.generateAnswerFeedback(answerId);
      console.log('Generate feedback result:', result);
      
      // Check if feedback generation was successful
      if (result && (result.ai_feedback || result.data)) {
        // Refetch complete interview results to get updated speech assessment
        await refreshInterviewData();
        toast.success('AI feedback and speech analysis generated!');
      } else {
        throw new Error(result.message || 'Failed to generate feedback');
      }
    } catch (error) {
      console.error('Feedback error:', error);
      toast.error(error.message || 'Failed to generate feedback');
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [answerId]: false }));
    }
  };

  const refreshInterviewData = async () => {
    try {
      // Refetch the complete interview results to get updated speech assessments
      const interviewResults = await apiService.getInterviewResults(interview.id);
      console.log('Refreshed interview results:', interviewResults);
      console.log('Interview results structure:', {
        hasData: !!interviewResults.data,
        hasCandidates: !!(interviewResults.data && interviewResults.data.candidates),
        hasResults: !!(interviewResults.data && interviewResults.data.results),
        keys: interviewResults.data ? Object.keys(interviewResults.data) : []
      });
      
      // The API returns results directly, not nested in data
      const candidates = interviewResults.results || interviewResults.data?.results || interviewResults.data?.candidates || [];
      console.log('Candidates array:', candidates);
      
      if (Array.isArray(candidates)) {
        // Find the current candidate in the refreshed data
        const refreshedCandidate = candidates.find(c => c.id === candidate.id);
        console.log('Found refreshed candidate:', refreshedCandidate);
        
        if (refreshedCandidate) {
          // Update local state with fresh data
          setAnswers(refreshedCandidate.answers || []);
          
          // Notify parent component with the complete refreshed candidate data
          if (onCandidateUpdate) {
            onCandidateUpdate(refreshedCandidate);
          }
          
          console.log('Interview data refreshed successfully');
        } else {
          console.warn('Candidate not found in refreshed data');
        }
      } else {
        console.warn('Candidates is not an array:', typeof candidates);
      }
    } catch (error) {
      console.error('Failed to refresh interview data:', error);
      // Don't show error toast as the main operation succeeded
    }
  };

  const generateAllFeedback = async () => {
    try {
      setGeneratingAll(true);
      
      // Generate feedback only for the current candidate's answers that don't have feedback
      const answersWithoutFeedback = answers.filter(answer => !answer.ai_feedback || answer.ai_feedback.trim() === '');
      
      if (answersWithoutFeedback.length === 0) {
        toast.info('All answers already have feedback');
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      // Generate feedback for each answer that doesn't have it
      for (const answer of answersWithoutFeedback) {
        try {
          const result = await apiService.generateAnswerFeedback(answer.id);
          console.log(`Generated feedback for answer ${answer.id}:`, result);
          successCount++;
          
          // Small delay to prevent overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`Failed to generate feedback for answer ${answer.id}:`, error);
          errorCount++;
        }
      }
      
      // Refetch complete interview results to get updated data
      await refreshInterviewData();
      
      if (successCount > 0) {
        toast.success(`Generated feedback and scores for ${successCount} answer${successCount > 1 ? 's' : ''}!`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to generate feedback for ${errorCount} answer${errorCount > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Generate all feedback error:', error);
      toast.error(error.message || 'Failed to generate batch feedback');
    } finally {
      setGeneratingAll(false);
    }
  };

  const overallScore = calculateScore(answers);

  return (
    <div className="space-y-6">
      {/* Candidate Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img
              className="h-16 w-16 rounded-full mr-4"
              src={candidate.candidate?.profile_picture || 
                   `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.candidate?.name || 'Unknown')}&background=3b82f6&color=fff`}
              alt={candidate.candidate?.name || 'Unknown'}
            />
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {candidate.candidate?.name || 'Unknown Candidate'}
              </h3>
              <p className="text-gray-600">{candidate.candidate?.email || 'No email'}</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${getScoreColor(overallScore)}`}>
              {answers && answers.some(answer => answer.score !== null && answer.score !== undefined) 
                ? `${overallScore}/100` 
                : 'No Score'}
            </div>
            <p className="text-sm text-gray-600">Overall Score</p>
          </div>
        </div>

        {/* Interview Stats */}
        <div className="mt-6 grid grid-cols-4 gap-4 pt-6 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {candidate.answered_questions || 0}
            </p>
            <p className="text-sm text-gray-600">Answered</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {candidate.total_questions || 0}
            </p>
            <p className="text-sm text-gray-600">Total Questions</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {candidate.started_at && candidate.completed_at 
                ? Math.round((new Date(candidate.completed_at) - new Date(candidate.started_at)) / 60000)
                : 0}
            </p>
            <p className="text-sm text-gray-600">Minutes</p>
          </div>
          <div className="text-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(candidate.status)}`}>
              {candidate.status}
            </span>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="mt-4 pt-4 border-t flex justify-end">
          <button
            onClick={generateAllFeedback}
            disabled={generatingAll}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex items-center"
          >
            {generatingAll ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing Audio & AI...
              </>
            ) : (
              '🤖 Generate All Feedback'
            )}
          </button>
        </div>
      </div>

      {/* Questions and Answers */}
      {answers && answers.length > 0 ? (
        <div className="card">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Interview Responses</h4>

          {/* Question Navigation */}
          <div className="mb-6">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {answers.map((answer, index) => (
                <button
                  key={answer.question_id || index}
                  onClick={() => setSelectedQuestion(index)}
                  className={`flex-shrink-0 px-3 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 ${
                    selectedQuestion === index
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>Q{index + 1}</span>
                  {answer.status === 'unanswered' ? (
                    <XCircleIcon className="h-3 w-3 text-red-500" />
                  ) : answer.score !== null && answer.score !== undefined ? (
                    <span className={`${getScoreColor(answer.score)}`}>
                      {answer.score}
                    </span>
                  ) : (
                    <CheckCircleIcon className="h-3 w-3 text-green-500" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Question Details */}
          {answers[selectedQuestion] && (
            <div className="space-y-4">
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Question:</h5>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {answers[selectedQuestion].question?.question_text || 'Question text not available'}
                </p>
                {answers[selectedQuestion].question?.skill_assessed && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Skill Assessed:</strong> {answers[selectedQuestion].question.skill_assessed}
                  </p>
                )}
              </div>
              <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 mb-2">Answer:</h5>
                {answers[selectedQuestion].status === 'unanswered' ? (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      <p className="text-red-800 font-medium">Question Not Answered</p>
                    </div>
                    <p className="text-red-600 text-sm mt-1">
                      The candidate did not provide an answer to this question.
                    </p>
                  </div>
                ) : (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {/* Show transcript if available, otherwise show answer text */}
                      {answers[selectedQuestion].speech_assessment?.transcript || 
                       answers[selectedQuestion].answer_text || 
                       'No answer provided'}
                    </p>
                    
                    {answers[selectedQuestion].speech_assessment && (
                      <div className="mt-3 flex items-center space-x-2">
                        <PlayIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-blue-600">
                          Audio transcribed: {answers[selectedQuestion].speech_assessment.transcript_source === 'auto' ? 'Automatically' : 'Manually'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* AI Feedback Section */}
              <div className="flex-1">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-900">AI Assessment:</h5>
                  {!answers[selectedQuestion]?.ai_feedback ? (
                    <button
                      onClick={() => generateSingleFeedback(answers[selectedQuestion].id)}
                      disabled={loadingFeedback[answers[selectedQuestion].id]}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                    >
                      {loadingFeedback[answers[selectedQuestion].id] ? 'Processing Audio & AI...' : '🤖 Generate Feedback'}
                    </button>
                  ) : (
                    <button
                      onClick={() => generateSingleFeedback(answers[selectedQuestion].id)}
                      disabled={loadingFeedback[answers[selectedQuestion].id]}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Regenerate
                    </button>
                  )}
                </div>

                {answers[selectedQuestion]?.ai_feedback ? (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Score:</span>
                      <span className={`text-lg font-bold ${getScoreColor(answers[selectedQuestion].score)}`}>
                        {answers[selectedQuestion].score !== null && answers[selectedQuestion].score !== undefined ? answers[selectedQuestion].score : 0}/100
                      </span>
                    </div>
                    <div className="bg-white p-3 rounded border">
                      <pre className="whitespace-pre-wrap text-sm text-gray-600 font-sans">
                        {answers[selectedQuestion].ai_feedback}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-500 text-sm italic">
                      No AI feedback generated yet. Click the button above to generate assessment.
                    </p>
                  </div>
                )}
              </div>
            </div>
              {/* Speech Analysis */}
              {answers[selectedQuestion].speech_assessment && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Speech Analysis:</h5>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Fluency:</span>
                        <span className="ml-2">{answers[selectedQuestion].speech_assessment.fluency_score !== null && answers[selectedQuestion].speech_assessment.fluency_score !== undefined ? answers[selectedQuestion].speech_assessment.fluency_score : 0}/100</span>
                      </div>
                      <div>
                        <span className="font-medium">Pronunciation:</span>
                        <span className="ml-2">{answers[selectedQuestion].speech_assessment.pronunciation_score !== null && answers[selectedQuestion].speech_assessment.pronunciation_score !== undefined ? answers[selectedQuestion].speech_assessment.pronunciation_score : 0}/100</span>
                      </div>
                      <div>
                        <span className="font-medium">Pace:</span>
                        <span className="ml-2">{answers[selectedQuestion].speech_assessment.pace_score !== null && answers[selectedQuestion].speech_assessment.pace_score !== undefined ? answers[selectedQuestion].speech_assessment.pace_score : 0}/100</span>
                      </div>
                      <div>
                        <span className="font-medium">Overall:</span>
                        <span className="ml-2">{answers[selectedQuestion].speech_assessment.overall_speech_score !== null && answers[selectedQuestion].speech_assessment.overall_speech_score !== undefined ? answers[selectedQuestion].speech_assessment.overall_speech_score : 0}/100</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 pt-2 border-t">
                {answers[selectedQuestion].submitted_at ? (
                  <>Answered on: {new Date(answers[selectedQuestion].submitted_at).toLocaleString()}</>
                ) : (
                  <>Question not answered</>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Responses Available</h3>
            <p className="text-gray-600">This candidate hasn't submitted any answers yet</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateResult;
//push
